import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
  useCallback,
} from "react";
import { io, Socket } from "socket.io-client";
import { TEST_SOCKET } from "../config";
import { useUserContext } from "./userContext";
import { useToastHelper } from "@/helpers/helperFunctions";
import { useQueryClient } from "@tanstack/react-query";

interface NotificationSocketContextTypes {
  socket: Socket | null;
  onMessage: (message: any) => void;
  registerOnMessage: (handler: (message: any) => void) => void;
  notificationCount: number;
}

export const ROOMS_CONST = {
  tradeNotifications: "web.trylevel2.com.trade_notifications",
};

const NotificationSocketContext =
  createContext<NotificationSocketContextTypes | null>(null);

export const useNotificationSocket = () => {
  const context = useContext(NotificationSocketContext);
  if (!context) {
    throw new Error("useNotificationSocket must be used within a provider");
  }
  return context;
};

interface NotificationSocketProviderProps {
  children: ReactNode;
}

// Socket singleton outside the component
let socketInstance: Socket | null = null;
if (!socketInstance) {
  socketInstance = io(TEST_SOCKET, {
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    auth: (cb) => {
      try {
        const user = JSON.parse(localStorage.getItem("user-info") || "{}");
        cb({ token: user?.token ? `Bearer ${user.token}` : undefined });
      } catch {
        cb({});
      }
    },
    autoConnect: false,
  });
}

export const NotificationSocketProvider = ({
  children,
}: NotificationSocketProviderProps) => {
  const [notificationSocketConnected, setNotificationSocketConnected] =
    useState(false);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [addedUser, setAddedUser] = useState<boolean>(false);

  const onMessageHandlerRef = useRef<(message: any) => void>(() => {});
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { toast } = useToastHelper();
  const { user } = useUserContext();
  const queryClient = useQueryClient();

  /** register message handler */
  const registerOnMessage = useCallback((handler: (message: any) => void) => {
    onMessageHandlerRef.current = handler;
  }, []);

  /** Actually add user to rooms */
  const addUserToRoom = useCallback(
    (userID: string) => {
      if (!socketInstance?.connected) {
        console.warn("Socket not connected yet, cannot add user to room");
        return;
      }
      // add user to both rooms
      const addRoom = (room: string) =>
        new Promise((resolve) => {
          socketInstance!.emit("addUserToRoom", room, (response: any) => {
            resolve(response);
          });
        });

      Promise.all([addRoom(`${ROOMS_CONST.tradeNotifications}.${userID}`)])
        .then(() => {
          setAddedUser(true);
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
          }
        })
        .catch((err) => {
          console.error("Failed to add user to rooms", err);
        });
    },
    [setAddedUser]
  );

  /** Retry logic for adding user */
  useEffect(() => {
    if (
      notificationSocketConnected &&
      user?.user_id &&
      !addedUser &&
      socketInstance?.connected
    ) {
      addUserToRoom(user.user_id);
      // schedule a retry if still not added after 3s
      retryTimeoutRef.current = setTimeout(() => {
        if (!addedUser) {
          console.warn("Retrying to add user to room…");
          addUserToRoom(user.user_id);
        }
      }, 3000);
    }
    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [notificationSocketConnected, addedUser, user?.user_id, addUserToRoom]);

  /** Socket connection + listeners */
  useEffect(() => {
    if (!socketInstance) return;

    const isInsideIframe = window.self !== window.top;
    if (isInsideIframe) {
      console.warn("Inside iframe → Skipping notification socket connection");
      return;
    }
    socketInstance.connect();

    socketInstance.on("connect", () => {
      console.info("Notification socket connected");
    });

    socketInstance.on("status", (data) => {
      if (data.status === "connected") {
        setNotificationSocketConnected(true);
      }
    });

    socketInstance.on("disconnect", () => {
      setNotificationSocketConnected(false);
      setAddedUser(false);
    });

    socketInstance.on("connect_error", (err) => {
      console.error("Socket connection error:", err);
    });

    socketInstance.on("getMessageFromServer", (data) => {
      onMessageHandlerRef.current?.(data);
    });

    socketInstance.on("order_update", (data) => {
      if (data?.type == "open") {
        setNotificationCount((prev) => prev + 1);
        toast.tradeOpened(data.strategy_name, data?.strategy_id);
      } else if (data?.type == "close") {
        toast.tradeClosed(data.strategy_name, data?.strategy_id);
      }
      queryClient.invalidateQueries({
        queryKey: ["get_live_strategy_full_order_book"],
      });
      queryClient.invalidateQueries({
        queryKey: ["get_live_strategy_order_book"],
      });
    });

    return () => {
      socketInstance?.off("connect");
      socketInstance?.off("disconnect");
      socketInstance?.off("connect_error");
      socketInstance?.off("status");
      socketInstance?.off("getMessageFromServer");
    };
  }, []);

  return (
    <NotificationSocketContext.Provider
      value={{
        socket: socketInstance,
        onMessage: onMessageHandlerRef.current,
        registerOnMessage,
        notificationCount,
      }}
    >
      {children}
    </NotificationSocketContext.Provider>
  );
};
